/**
 * Shared background classes so the left rail and timeline grid rows stay aligned:
 * project rows are darker than group rows and carry a stronger top divider.
 */
import {
  SCHEDULE_GROUP_CELL_BG,
  SCHEDULE_GROUP_CELL_BORDER,
  SCHEDULE_GROUP_CELL_BORDER_THICKNESS,
  SCHEDULE_GROUP_WEEKEND_BG,
  SCHEDULE_OUT_OF_JOB_RANGE_BG,
  SCHEDULE_PLACEHOLDER_CELL_BG,
  SCHEDULE_PROJECT_CELL_BG,
  SCHEDULE_PROJECT_CELL_BORDER,
  SCHEDULE_PROJECT_CELL_BORDER_THICKNESS,
  SCHEDULE_PROJECT_CELL_TOP_BORDER,
  SCHEDULE_PROJECT_CELL_TOP_BORDER_THICKNESS,
  SCHEDULE_PROJECT_WEEKEND_BG,
} from "../style";

/** `border-gray-*` semantic token → `border-t-gray-*` for project row top edge. */
const SCHEDULE_PROJECT_CELL_TOP_BORDER_COLOR_CLASS =
  SCHEDULE_PROJECT_CELL_TOP_BORDER.replace(/^border-/, "border-t-");

/** `border-gray-*` semantic token → `border-r-gray-*` for project row right edge. */
const SCHEDULE_PROJECT_CELL_RIGHT_BORDER_COLOR_CLASS =
  SCHEDULE_PROJECT_CELL_BORDER.replace(/^border-/, "border-r-");

/** @type {Record<string, string>} */
export const SCHEDULE_ROW_STRIPE = {
  /** Project header row (depth 0) */
  project: SCHEDULE_PROJECT_CELL_BG,
  /** Schedule group row under a project */
  group: SCHEDULE_GROUP_CELL_BG,
  /** Empty / placeholder grid */
  placeholder: SCHEDULE_PLACEHOLDER_CELL_BG,
};

/** Grid cell overlays (weekend / outside job active range) — composed on top of row stripe logic. */
/** @type {Record<string, string>} */
export const SCHEDULE_GRID_CELL_BG = {
  outOfJobRange: SCHEDULE_OUT_OF_JOB_RANGE_BG,
  weekendProject: SCHEDULE_PROJECT_WEEKEND_BG,
  weekendGroup: SCHEDULE_GROUP_WEEKEND_BG,
  weekendFallback: SCHEDULE_OUT_OF_JOB_RANGE_BG,
};

/** Body cells draw only their top and right edges so adjacent borders do not double. */
export const SCHEDULE_GROUP_CELL_BORDER_CLASS = `border-t-[${SCHEDULE_GROUP_CELL_BORDER_THICKNESS}px] border-r-[${SCHEDULE_GROUP_CELL_BORDER_THICKNESS}px] ${SCHEDULE_GROUP_CELL_BORDER}`;

/** Top + thin-right combo omitted — Friday-before-gap draws its own thick `border-r-*`. */
export const SCHEDULE_GROUP_CELL_BORDER_TOP_ONLY_CLASS = `border-t-[${SCHEDULE_GROUP_CELL_BORDER_THICKNESS}px] ${SCHEDULE_GROUP_CELL_BORDER}`;

/** Project surfaces: thick top uses {@link SCHEDULE_PROJECT_CELL_TOP_BORDER}; thin right uses {@link SCHEDULE_PROJECT_CELL_BORDER}. */
export const SCHEDULE_PROJECT_CELL_BORDER_CLASS = `border-t-[${SCHEDULE_PROJECT_CELL_TOP_BORDER_THICKNESS}px] ${SCHEDULE_PROJECT_CELL_TOP_BORDER_COLOR_CLASS} border-r-[${SCHEDULE_PROJECT_CELL_BORDER_THICKNESS}px] ${SCHEDULE_PROJECT_CELL_RIGHT_BORDER_COLOR_CLASS}`;

/** Project variant without right edge — used before hidden-weekend column seams. */
export const SCHEDULE_PROJECT_CELL_BORDER_TOP_ONLY_CLASS = `border-t-[${SCHEDULE_PROJECT_CELL_TOP_BORDER_THICKNESS}px] ${SCHEDULE_PROJECT_CELL_TOP_BORDER_COLOR_CLASS}`;

/**
 * @param {{ rowType?: 'project'|'group', id?: string }|null|undefined} row
 * @returns {string} Tailwind bg class
 */
export function getScheduleRowStripeClass(row) {
  if (!row || row.id === "schedule-empty-grid-row") {
    return SCHEDULE_ROW_STRIPE.placeholder;
  }
  if (row.rowType === "project") {
    return SCHEDULE_ROW_STRIPE.project;
  }
  if (row.rowType === "group") {
    return SCHEDULE_ROW_STRIPE.group;
  }
  return SCHEDULE_ROW_STRIPE.placeholder;
}

/**
 * Border classes for schedule row/cell surfaces.
 *
 * @param {{ rowType?: 'project'|'group', id?: string }|null|undefined} row
 * @param {{ omitRight?: boolean }} [options] Omit thin right edge so callers can paint a custom right seam (e.g. Fri→Mon gap).
 * @returns {string}
 */
export function getScheduleRowCellBorderClass(row, options = {}) {
  const omitRight = Boolean(options.omitRight);
  if (row?.rowType === "project") {
    return omitRight
      ? SCHEDULE_PROJECT_CELL_BORDER_TOP_ONLY_CLASS
      : SCHEDULE_PROJECT_CELL_BORDER_CLASS;
  }
  return omitRight
    ? SCHEDULE_GROUP_CELL_BORDER_TOP_ONLY_CLASS
    : SCHEDULE_GROUP_CELL_BORDER_CLASS;
}

/**
 * Cell background for a grid cell: row stripe + weekend / out-of-job-range overlays.
 *
 * @param {Object} opts
 * @param {{ rowType?: 'project'|'group', id?: string }|null|undefined} opts.row
 * @param {boolean} opts.isWeekend
 * @param {boolean} opts.isPlaceholder
 * @param {Object|null} [opts.job]
 * @param {boolean} opts.inJobRange
 * @returns {string} Tailwind classes (single bg-* token or compatible combo)
 */
export function getScheduleGridCellBgClass({
  row,
  isWeekend,
  isPlaceholder,
  job,
  inJobRange,
}) {
  const rowType = row?.rowType;

  if (!isPlaceholder && job && !inJobRange) {
    return SCHEDULE_GRID_CELL_BG.outOfJobRange;
  }

  if (isWeekend) {
    if (rowType === "project") {
      return SCHEDULE_GRID_CELL_BG.weekendProject;
    }
    if (rowType === "group") {
      return SCHEDULE_GRID_CELL_BG.weekendGroup;
    }
    return SCHEDULE_GRID_CELL_BG.weekendFallback;
  }

  return getScheduleRowStripeClass(row);
}
